import type { BusinessContent } from "~/types/BusinessContent";
import type { DynamicContent } from "~/types/DynamicContent";
import type { SummaryFormFields } from "~/types/FormTypes";
import type { Token } from "~/types/Token";
import type { UploadContent } from "~/types/UploadContent";

const base = () => {
  const { apiBase } = useRuntimeConfig().public;
  return apiBase || window.location.origin;
};

const LOCALE_BASE_URL = () => `${base()}/locales`;
const FORMS_BASE_URL = () => `${base()}/Forms`;
const UPLOAD_BASE_URL = () => `${base()}/Upload`;
const BUSINESS_BASE_URL = () => `${base()}/Business`;

export const getDynamicContent = (): Promise<DynamicContent | null> =>
  $fetch<DynamicContent>(`${LOCALE_BASE_URL()}/dynamic-da.json`, {
    query: { t: Date.now() },
  }).catch((error) => {
    console.error("Failed to fetch content:", error);
    return null;
  });

export const getUploadContent = (): Promise<UploadContent | null> =>
  $fetch<UploadContent>(`${LOCALE_BASE_URL()}/content-upload-da.json`, {
    query: { t: Date.now() },
  }).catch((error) => {
    console.error("Failed to fetch content:", error);
    return null;
  });

export const getBusinessContent = (): Promise<BusinessContent | null> =>
  $fetch<BusinessContent>(`${LOCALE_BASE_URL()}/content-business-da.json`, {
    query: { t: Date.now() },
  }).catch((error) => {
    console.error("Failed to fetch content:", error);
    return null;
  });

export const getToken = (): Promise<Token | null> => {
  const tokenUrl = `${FORMS_BASE_URL()}/${process.env.NODE_ENV === "development" ? "DummyToken" : "GetToken"}`;
  return $fetch<Token>(tokenUrl).catch((error) => {
    console.error("Failed to fetch token:", error);
    return null;
  });
};

export const submit = async (
  data: FormData,
  url: string,
  token: string,
): Promise<boolean> => {
  try {
    const response = await fetch(`${FORMS_BASE_URL()}/${url}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: data,
    });

    if (response.ok) {
      return true;
    }
    console.error("Failed to post data, response status:", response.status);
    return false;
  } catch (error) {
    console.error("Failed to post data", error);
    return false;
  }
};

export const getSummary = async (
  token: string,
): Promise<SummaryFormFields | null> => {
  try {
    const response = await fetch(`${FORMS_BASE_URL()}/Summary`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      return response.json();
    }
    console.error("Failed to get data, response status:", response.status);
    return null;
  } catch (error) {
    console.error("Failed to get data", error);
    return null;
  }
};

export const submitApplication = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(`${FORMS_BASE_URL()}/SubmitForm`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      return true;
    }
    console.error("Failed to get data, response status:", response.status);
    return false;
  } catch (error) {
    console.error("Failed to get data", error);
    return false;
  }
};

export const submitUploadData = async (data: FormData): Promise<boolean> => {
  try {
    const response = await fetch(`${UPLOAD_BASE_URL()}/Submit`, {
      method: "POST",
      body: data,
    });

    if (response.ok) {
      return true;
    }
    console.error("Failed to post data, response status:", response.status);
    return false;
  } catch (error) {
    console.error("Failed to post data", error);
    return false;
  }
};

export const submitBusinessData = async (data: FormData): Promise<boolean> => {
  try {
    const response = await fetch(`${BUSINESS_BASE_URL()}/Submit`, {
      method: "POST",
      body: data,
    });

    if (response.ok) {
      return true;
    }
    console.error("Failed to post data, response status:", response.status);
    return false;
  } catch (error) {
    console.error("Failed to post data", error);
    return false;
  }
};
